import React, { useEffect, useState } from 'react'
import { Box, Select, FormControl, Stack, Text, Input } from '@/components/lib'
import BMITag from '../includes/BMITag'
import utils from '../../utils/utils'
import useStore from '../../store'

// Value = { bmi, height, weight }. Height in cm. Weight in kg.
export default function BMIEditor ({ value, onChange }) {
  const { settings, changeSetting } = useStore()
  const measurement = settings?.bmiMeasureType || 'metric'
  const [bmi, setBmi] = useState()
  const [weight, setWeight] = useState()
  const [height, setHeight] = useState()
  const [weightPounds, setWeightPounds] = useState()
  const [weightOunces, setWeightOunces] = useState()
  const [heightFeet, setHeightFeet] = useState()
  const [heightInches, setHeightInches] = useState()

  useEffect(() => {
    const heightVal = value?.height?.toString() || ''
    setHeight(heightVal)
    const imperialHeight = utils.cmToFeet(heightVal || 0)
    if (imperialHeight) {
      setHeightFeet(imperialHeight.feet)
      setHeightInches(imperialHeight.inches)
    }

    const weightVal = value?.weight?.toString() || ''
    setWeight(weightVal)
    const imperialWeight = utils.kToLbs(weightVal || 0)
    if (imperialWeight) {
      setWeightPounds(imperialWeight.pounds)
      setWeightOunces(imperialWeight.ounces)
    }

    calculateBmi(value?.weight, value?.height)
  }, [value])

  const calculateBmi = (weight, height) => {
    let newBmi = 0
    if (weight && height) {
      const heightInMetres = parseFloat(height) / 100
      newBmi = parseFloat(weight) / (heightInMetres * heightInMetres)
    }
    setBmi(newBmi)
    return newBmi
  }

  const handleChange = (weight, height) => {
    if (!height && !weight) {
      onChange(null)
    } else if (height && weight) {
      onChange({ height, weight, bmi: calculateBmi(weight, height) })
    }
  }

  const updateMetricHeight = cm => {
    if (cm === '' || parseFloat(cm) >= 0) {
      setHeight(cm)
      handleChange(weight, cm)
    }
    const imperial = utils.cmToFeet(cm || 0)
    if (imperial) {
      setHeightFeet(imperial.feet)
      setHeightInches(imperial.inches)
    }
  }
  const updateMetricWeight = kg => {
    if (kg === '' || parseFloat(kg) >= 0) {
      setWeight(kg)
      handleChange(kg, height)
    }
    const imperial = utils.kToLbs(kg || 0)
    if (imperial) {
      setWeightPounds(imperial.pounds)
      setWeightOunces(imperial.ounces)
    }
  }

  const updateImperialHeight = (feet, inches) => {
    setHeightFeet(feet)
    if (inches === '' || (parseFloat(inches) >= 0 && parseFloat(inches) < 12)) {
      setHeightInches(inches)
    }
    if (!feet && !inches) {
      setHeight('')
      handleChange(weight, '')
    } else if (feet || inches) {
      const cm = utils.feetToCm(feet || 0, inches || 0)
      setHeight(cm)
      handleChange(weight, cm)
    }
  }
  const updateImperialWeight = (pounds, ounces) => {
    setWeightPounds(pounds)
    if (ounces === '' || (parseFloat(ounces) >= 0 && parseFloat(ounces) < 16)) {
      setWeightOunces(ounces)
    }
    if (!pounds && !ounces) {
      setWeight('')
      handleChange('', height)
    } else if (pounds || ounces) {
      const kg = utils.lbToK(pounds || 0, ounces || 0)
      setWeight(kg)
      handleChange(kg, height)
    }
  }

  return (
    <Stack gap={5}>
      <Box>
        <Select
          w='200px' placeholder='Measurement type...' size='xs' bg='bg.surface'
          value={measurement}
          onChange={e => changeSetting('bmiMeasureType', e.target.value || 'metric')}
        >
          <option value='metric'>Metric (kg & cm)</option>
          <option value='imperial'>Imperial (lb & ft)</option>
        </Select>
      </Box>

      <Stack direction='row' alignItems='center' gap={5}>
        <Stack>
          {measurement === 'metric' &&
            <>
              <FormControl bg='bg.accent' rounded='md' w='150px' label='Height'>
                <Stack direction='row' alignItems='center'>
                  <Input width='80px' bg='bg.surface' size='sm' value={height ?? ''} onChange={e => updateMetricHeight(e.target.value.replace(/[^\d.]/g, ''))} />
                  <Text>cm</Text>
                </Stack>
              </FormControl>
              <FormControl bg='bg.accent' rounded='md' w='150px' label='Weight'>
                <Stack direction='row' alignItems='center'>
                  <Input width='80px' bg='bg.surface' size='sm' value={weight ?? ''} onChange={e => updateMetricWeight(e.target.value.replace(/[^\d.]/g, ''))} />
                  <Text>kg</Text>
                </Stack>
              </FormControl>
            </>}

          {measurement === 'imperial' &&
            <>
              <FormControl bg='bg.accent' rounded='md' w='270px' label='Height'>
                <Stack direction='row' alignItems='center' spacing={5}>
                  <Stack direction='row' alignItems='center'>
                    <Input width='60px' bg='bg.surface' size='sm' value={heightFeet ?? ''} onChange={e => updateImperialHeight(e.target.value.replace(/[^\d.]/g, ''), heightInches)} />
                    <Text>feet</Text>
                  </Stack>
                  <Stack direction='row' alignItems='center'>
                    <Input width='60px' bg='bg.surface' size='sm' value={heightInches ?? ''} onChange={e => updateImperialHeight(heightFeet, e.target.value.replace(/[^\d.]/g, ''))} />
                    <Text>inches</Text>
                  </Stack>
                </Stack>
              </FormControl>

              <FormControl bg='bg.accent' rounded='md' p={2} w='270px' label='Weight'>
                <Stack direction='row' alignItems='center' spacing={5}>
                  <Stack direction='row' alignItems='center'>
                    <Input width='60px' bg='bg.surface' size='sm' value={weightPounds ?? ''} onChange={e => updateImperialWeight(e.target.value.replace(/[^\d.]/g, ''), weightOunces)} />
                    <Text>lbs</Text>
                  </Stack>
                  <Stack direction='row' alignItems='center'>
                    <Input width='60px' bg='bg.surface' size='sm' value={weightOunces ?? ''} onChange={e => updateImperialWeight(weightPounds, e.target.value.replace(/[^\d.]/g, ''))} />
                    <Text>oz</Text>
                  </Stack>
                </Stack>
              </FormControl>
            </>}
        </Stack>

        <BMITag bmi={bmi} asCircular />
      </Stack>
    </Stack>
  )
}
